import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-geosearch/dist/geosearch.css";
import "leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css";
import "leaflet-defaulticon-compatibility";
import "driver.js/dist/driver.css";
import React, { useRef, useEffect, useState, useContext } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  Marker,
  useMap,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import GlobalContext from "../../context/GlobalContext";
import { createLocation, updateLocation } from "../../api/LocationsApi";
import swal from "../../services/swalConfig";
import { driver } from "driver.js";

import { tr } from "../../i18n";
function SearchControl({ provider, onResult }) {
  const map = useMap();

  useEffect(() => {
    const searchControl = new GeoSearchControl({
      provider,
      style: "bar",
      showMarker: false,
      showPopup: true,
      retainZoomLevel: false,
      animateZoom: true,
      autoClose: true,
      searchLabel: "Enter address",
    });

    map.addControl(searchControl);

    map.on("geosearch/showlocation", (result) => {
      const { location } = result;
      onResult([location.y, location.x]); // simpan lat,lng
    });

    return () => map.removeControl(searchControl);
  }, [map, provider, onResult]);

  return null;
}

export default function ModalLocation({ show, handleClose, onSuccess, data }) {
  const { user, setLoading } = useContext(GlobalContext);
  const featureGroupRef = useRef(null);
  const [polygonCoords, setPolygonCoords] = useState([]);
  const [searchMarker, setSearchMarker] = useState(null);
  const [locationName, setLocationName] = useState("");
  const editableFGRef = useRef(null);
  const isEdit = !!data;

  const provider = new OpenStreetMapProvider();

  useEffect(() => {
    if (show && data) {
      setLocationName(data.coordinate_name);

      // Parse JSON string → array
      let polygon = data.coordinate_polygon;
      if (typeof polygon === "string") {
        polygon = JSON.parse(polygon);
      }

      // Leaflet = [lat, lng]
      const latlngs = polygon.map((p) => [p[1], p[0]]);

      setTimeout(() => {
        const drawnItems = featureGroupRef.current;
        drawnItems.clearLayers();

        const L = window.L;
        const polygonLayer = L.polygon(latlngs).addTo(drawnItems);

        setPolygonCoords(latlngs.map(([lat, lng]) => ({ lat, lng })));

        // Auto center
        featureGroupRef.current._map.fitBounds(polygonLayer.getBounds());
      }, 300);
    }
  }, [show, data]);

  // Handle create polygon
  const _onCreate = (e) => {
    const layer = e.layer;
    const drawnItems = featureGroupRef.current;
    drawnItems.clearLayers();
    drawnItems.addLayer(layer);

    const coords = layer.getLatLngs()[0].map((point) => ({
      lat: point.lat,
      lng: point.lng,
    }));
    setPolygonCoords(coords);
  };

  const handleSave = async () => {
    if (!locationName.trim()) {
      alert(tr("attendance:please_enter_a_location_name"));
      return;
    }

    if (polygonCoords.length === 0) {
      alert(tr("attendance:please_draw_a_polygon_first"));
      return;
    }

    try {
      setLoading(true);
      const dbCoords = polygonCoords.map((point) => [point.lng, point.lat]);

      const payload = {
        company_id: user.workspace.company_id,
        coordinate_name: locationName,
        coordinate_polygon: dbCoords,
        ...(isEdit && { coordinate_id: data.coordinate_id }),
      };

      let response;

      if (!isEdit) {
        console.log("Create");
        response = await createLocation(payload);
      } else {
        console.log("Update");
        response = await updateLocation(payload);
      }

      if (response.status === 200) {
        onSuccess();
        handleClose();
        setPolygonCoords([]);
        setLocationName("");
      }

      swal.fire({
        title: response.status === 200 ? tr("shared:success") : tr("common:errors.oops"),
        text: response.message,
        icon: response.status === 200 ? "success" : "error",
        showCancelButton: false,
      });
    } catch (error) {
      console.log(error);
      swal.fire({
        title: tr("common:errors.oops"),
        text: tr("common:errors.generic"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOnClose = () => {
    handleClose();
    setPolygonCoords([]);
    setLocationName("");
  };

  const handleOnDeleted = () => {
    setPolygonCoords([]);

    // Reset area agar bisa draw baru
    editableFGRef.current?.clearLayers();
  };

  // Start guided tour
  const startTour = () => {
    const tour = driver({
      showProgress: true,
      steps: [
        {
          element: "#locationNameInput",
          popover: {
            title: tr("attendance:step_1"),
            description: tr("attendance:enter_the_name_of_the_location"),
          },
        },
        {
          element: ".leaflet-control-geosearch > form",
          popover: {
            title: tr("attendance:step_2"),
            description: tr("attendance:search_for_an_address_or_place"),
          },
        },
        {
          element: ".leaflet-draw-draw-polygon",
          popover: {
            title: tr("attendance:step_3"),
            description: tr("attendance:click_here_to_start_drawing_a"),
          },
        },
        {
          element: ".leaflet-draw-edit-edit",
          popover: {
            title: tr("attendance:step_4"),
            description: tr("attendance:use_this_button_to_edit_or"),
          },
        },
        {
          element: ".leaflet-draw-edit-remove",
          popover: {
            title: tr("attendance:step_5"),
            description: tr("attendance:use_this_button_to_delete_a"),
          },
        },
        {
          element: "#saveLocationBtn",
          popover: {
            title: tr("attendance:final_step"),
            description: tr("attendance:click_here_to_save_the_location"),
          },
        },
      ],
    });
    tour.drive();
  };

  // Jalankan tour hanya pertama kali modal dibuka
  useEffect(() => {
    if (show && !localStorage.getItem("locationTourShown")) {
      setTimeout(() => {
        startTour();
        localStorage.setItem("locationTourShown", "true");
      }, 500); // kasih delay biar modal & map render dulu
    }
  }, [show]);

  return (
    <Modal show={show} onHide={handleOnClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{tr("attendance:set_location_area")}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ height: "75vh", display: "flex", flexDirection: "column" }}
      >
        {/* Input nama lokasi */}
        <Form.Group
          className="mb-3 d-flex align-items-center"
          id="locationNameInput"
        >
          <Form.Label className="me-2 mb-0" style={{ whiteSpace: "nowrap" }}>
            {tr("attendance:location_name")}{" "} <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder={tr("attendance:enter_location_name")}
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
          />
        </Form.Group>

        {/* Map */}
        <div style={{ height: "500px" }}>
          <MapContainer
            center={[-6.2, 106.816]} // Jakarta default
            zoom={13}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <SearchControl provider={provider} onResult={setSearchMarker} />

            {searchMarker && <Marker position={searchMarker} />}

            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topright"
                draw={{
                  rectangle: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                }}
                onDeleted={handleOnDeleted}
                onCreated={_onCreate}
                onEdited={(e) => {
                  const layer = e.layers.getLayers()[0];
                  const coords = layer.getLatLngs()[0].map((pt) => ({
                    lat: pt.lat,
                    lng: pt.lng,
                  }));
                  setPolygonCoords(coords);
                }}
              />
            </FeatureGroup>
          </MapContainer>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="me-auto">
          <Button variant="outline-dark" onClick={startTour}>
            {tr("shared:show_tutorial")}
          </Button>
        </div>
        <Button variant="secondary" onClick={handleOnClose}>
          {tr("common:actions.cancel")}
        </Button>
        <Button id="saveLocationBtn" variant="primary" onClick={handleSave}>
          {tr("attendance:save_location")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
